import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { getArrayOfEmails, isEmail } from 'utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Button } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Form, Field } from 'react-final-form';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';

import './AddSellSideColleague.styles.scss';

type AddSellSideColleagueProps = {
  className?: string;
};

const AddSellSideColleague: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AddSellSideColleagueProps
> = (props, ref) => {
  const { className } = props;

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const classes = classNames('add-sell-side-colleague', className);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    doesntHaveAccess,
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { setBuySideGroups, setSellSideMembers } = useContext(MembersContext);

  const onInviteMembers = useCallback(
    async (values: {
      sellSideMembers: string[];
      sellSideMembersUnsubmited: string;
    }) => {
      const { sellSideMembers: sellSideMembersVal, sellSideMembersUnsubmited } =
        values;

      try {
        const {
          data: { sellSideMembers, buySideGroups },
        } = await dataRoomApi.members.inviteSellSideMembers(_id, {
          sellSideMembers: getArrayOfEmails(
            sellSideMembersVal,
            sellSideMembersUnsubmited,
          ),
        });

        setSellSideMembers(sellSideMembers);
        setBuySideGroups(buySideGroups);

        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, drawer, popServerError, setBuySideGroups, setSellSideMembers],
  );

  if (doesntHaveAccess) return null;

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      <p className="drawer-title">{t('addNewMembersToSellSide')}</p>
      <Form
        onSubmit={onInviteMembers}
        initialValues={{ sellSideMembers: [] }}
        render={({
          handleSubmit,
          submitting,
          values: { sellSideMembers, sellSideMembersUnsubmited },
        }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="sellSideMembers"
              required
              label={t('emailTextareaFieldLabel')}
              component={TextareaEmailsField}
              description={t('weAreGoingToSendInvitationsViaEmail')}
            />
            <div className="actions">
              <Button
                variant="link"
                disabled={submitting}
                onClick={() => drawer?.setOpen(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting ||
                  (!sellSideMembers.length &&
                    !isEmail(sellSideMembersUnsubmited))
                }
              >
                {t('confirm')}
              </Button>
            </div>
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(AddSellSideColleague);
